import React, { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Zap, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { OrderInputs, BaseRows, Row, multiInvalid, PenaltyRows, RainRows, rainInvalid } from "@/components/OrderBreakdownRows";

const todayStr = new Date().toISOString().slice(0, 10);
const empty = { date: todayStr, orders: "", multi_orders: "", denial_orders: "0", cancelled_orders: "0", total_distance: "", extra_distance: "", rain_orders: "0", other_incentive: "", other_adjustment: "", remarks: "" };

export function QuickEntryDrawer() {
  const { quickEntryDp, closeQuickEntry, role } = useApp();
  const dp = quickEntryDp;
  const [form, setForm] = useState(empty);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (dp) { setForm(empty); setPreview(null); } }, [dp]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doPreview = useCallback(async (f) => {
    try {
      const { data } = await api.post("/daily/preview", {
        dp_id: dp?.id, date: f.date,
        orders: Number(f.orders || 0), multi_orders: Number(f.multi_orders || 0), denial_orders: Number(f.denial_orders || 0), cancelled_orders: Number(f.cancelled_orders || 0), rain_orders: Number(f.rain_orders || 0),
        extra_distance: Number(f.extra_distance || 0),
        other_incentive: Number(f.other_incentive || 0),
        other_adjustment: Number(f.other_adjustment || 0),
      });
      setPreview(data);
    } catch (e) { /* noop */ }
  }, [dp]);

  useEffect(() => {
    if (!dp) return;
    const t = setTimeout(() => doPreview(form), 180);
    return () => clearTimeout(t);
  }, [form, dp, doPreview]);

  const save = async (force = false) => {
    setSaving(true);
    try {
      await api.post("/daily", {
        dp_id: dp.id, date: form.date || todayStr, orders: Number(form.orders || 0),
        multi_orders: Number(form.multi_orders || 0),
        denial_orders: Number(form.denial_orders || 0), cancelled_orders: Number(form.cancelled_orders || 0),
        total_distance: Number(form.total_distance || 0),
        extra_distance: Number(form.extra_distance || 0), rain_orders: Number(form.rain_orders || 0),
        other_incentive: Number(form.other_incentive || 0),
        other_adjustment: Number(form.other_adjustment || 0),
        remarks: form.remarks, force_edit: force, _user: role,
      });
      toast.success(`Daily activity saved for ${dp.full_name}`, { description: `${form.orders} orders (${Number(form.multi_orders || 0)} multi) · ${inr(preview?.total_earnings)}` });
      window.dispatchEvent(new CustomEvent("ct-refresh"));
      closeQuickEntry();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.warning("Record exists for this date", {
          description: e.response.data.detail,
          action: { label: "Edit existing", onClick: () => save(true) },
        });
      } else {
        toast.error(e?.response?.data?.detail || "Failed to save activity");
      }
    } finally { setSaving(false); }
  };

  const slabReach = Number(form.orders || 0) >= 45 ? "₹70/order slab achieved" :
    Number(form.orders || 0) >= 26 ? "₹65/order slab achieved" :
    Number(form.orders || 0) >= 1 ? "₹60/order slab" : "";

  return (
    <Sheet open={!!dp} onOpenChange={(o) => !o && closeQuickEntry()}>
      <SheetContent className="w-full overflow-y-auto bg-card sm:max-w-lg" data-testid="quick-entry-drawer">
        {dp && (
          <>
            <SheetHeader>
              <SheetTitle className="font-head flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" /> Add Daily Activity
              </SheetTitle>
              <p className="text-sm text-muted-foreground">{dp.full_name} · <span className="font-mono">{dp.fe_id}</span> · {dp.location}</p>
            </SheetHeader>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Delivery Date</Label>
                <Input data-testid="quick-entry-date" type="date" max={todayStr} value={form.date}
                  onChange={(e) => set("date", e.target.value)} />
                <p className="mt-1 text-xs text-muted-foreground">Select today or any earlier date to log past deliveries.</p>
              </div>
              <OrderInputs form={form} set={set} prefix="quick-entry" preview={preview} />
              {slabReach && <p className="col-span-2 -mt-2 flex items-center gap-1 text-xs font-medium text-emerald-500"><TrendingUp className="h-3 w-3" />{slabReach} (by total orders)</p>}
              <div><Label>Total Distance (KM)</Label><Input type="number" value={form.total_distance} onChange={(e) => set("total_distance", e.target.value)} /></div>
              <div><Label>Extra Distance (KM)</Label><Input data-testid="quick-entry-extra-distance" type="number" value={form.extra_distance} onChange={(e) => set("extra_distance", e.target.value)} /></div>
              <div><Label>Other Incentive (₹)</Label><Input type="number" value={form.other_incentive} onChange={(e) => set("other_incentive", e.target.value)} /></div>
              <div><Label>Other Adjustment (₹)</Label><Input type="number" value={form.other_adjustment} onChange={(e) => set("other_adjustment", e.target.value)} /></div>
              <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} /></div>
            </div>

            {preview && (
              <div className="mt-5 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="quick-entry-breakdown">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Earning Breakdown · Slab {preview.slab_selected}</p>
                <div className="mt-3 space-y-1.5 text-sm">
                  <BaseRows p={preview} />
                  <RainRows p={preview} />
                  <Row l={`Extra Distance (${preview.extra_distance_km} × ₹${preview.extra_distance_rate})`} v={inr(preview.extra_distance_payout)} />
                  <Row l="Other Incentive" v={inr(preview.other_incentive)} />
                  <Row l="Other Adjustment" v={inr(preview.other_adjustment)} />
                  <PenaltyRows p={preview} />
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-primary/20 pt-3">
                  <span className="font-head text-sm font-bold uppercase tracking-wide">Final Total Earnings</span>
                  <span data-testid="quick-entry-total" className="font-head text-2xl font-bold tabular text-emerald-500">{inr(preview.total_earnings)}</span>
                </div>
                {preview.franchise_earning != null && (
                  <div className="mt-3 flex items-center justify-between rounded-lg bg-violet-500/10 px-3 py-2" data-testid="quick-entry-franchise">
                    <span className="text-xs text-violet-500">Franchise ({preview.franchise_referral}) · {preview.order_count} × ₹{preview.franchise_rate}</span>
                    <span className="tabular text-sm font-bold text-violet-500">{inr(preview.franchise_earning)}</span>
                  </div>
                )}
              </div>
            )}

            <Button data-testid="quick-entry-submit-btn" onClick={() => save(false)} disabled={saving || !form.orders || multiInvalid(form) || rainInvalid(form)}
              className="mt-5 h-12 w-full text-base font-semibold active:scale-[0.98]">
              {saving ? "Saving…" : "Save Daily Activity"}
            </Button>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
